// ─────────────────────────────────────────────
//  MessagesPage — the message centre, as a page.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The landing dashboard's fourth door. It is the SAME two components the 💬
//  panel and the "My Rooms" page already use — ChannelList (today's derived room
//  channels, groups and roster DMs) and ConversationView (thread + composer) —
//  laid out full-page instead of in a drawer.
//
//  ⚠️ THIS FILE USED TO BE SOMETHING ELSE. It was a standalone DM screen that
//  read `messages` from Firestore directly and listed every user in the facility
//  as a contact. That surface was folded into the messaging panel (D-01) and the
//  route was redirected to the board, leaving 200 lines of second, diverging
//  implementation of chat behind it. Rebuilding on the shared components is what
//  makes reviving the route safe: read rules, unread counting, group visibility
//  and the roster-driven contact list all stay in ONE place.
//
//  Master/detail side by side at ≥760px; below that the list swaps to the
//  conversation and back, the same rule MessagingPanel uses at 640px (a page has
//  a topbar and page padding to pay for, so it needs the room sooner).
//
//  Not for everyone: the sandbox has no second person to message, and observers
//  and board managers have messaging revoked in the rules, not merely hidden.
//  Each gets a sentence rather than an empty inbox — an empty inbox reads as a
//  broken feature.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { unlockAudioOnFirstGesture } from "../audio/chime";
import { isSandboxBoot } from "../sandbox/mode";
import ChannelList from "../components/ChannelList";
import ConversationView from "../components/ConversationView";
import ChimeMuteToggle from "../components/ChimeMuteToggle";
import NavMenu from "../components/NavMenu";
import { Avatar, SignOutButton } from "../components";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

function Notice({ title, body, onBack }) {
  return (
    <main style={s.noticeWrap}>
      <div style={s.notice}>
        <h1 style={s.noticeTitle}>{title}</h1>
        <p style={s.noticeBody}>{body}</p>
        <button type="button" style={s.cta} onClick={onBack}>Open the board</button>
      </div>
    </main>
  );
}

export default function MessagesPage() {
  const navigate = useNavigate();
  const { user, isDoctor, isObserver, isBoardManager } = useAuth();
  const [selection, setSelection] = useState(null);
  const isNarrow = useIsMobile(760);

  // The first gesture on this page unlocks audio so an incoming-message chime
  // can play (D-03) — browsers refuse to start audio without one.
  const pick = (next) => {
    unlockAudioOnFirstGesture();
    setSelection(next);
  };

  const noMessaging = isObserver || isBoardManager;
  const sandbox = isSandboxBoot();

  // On a narrow screen the two panes swap; on a wide one both are always up.
  const showList = !isNarrow || !selection;
  const showThread = !isNarrow || !!selection;

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <NavMenu />
        <div style={s.topbarRight}>
          {!noMessaging && !sandbox && <ChimeMuteToggle />}
          <Avatar user={user} isDoctor={isDoctor} onClick={() => navigate("/profile")} />
          <SignOutButton />
        </div>
      </div>

      {noMessaging ? (
        <Notice
          title="Messages aren't part of this account"
          body="This account monitors the board. Messaging is for the clinicians on today's roster."
          onBack={() => navigate("/board")}
        />
      ) : sandbox ? (
        <Notice
          title="Messages need a team"
          body="The sandbox runs on this device alone, so there is nobody to message. Everything else works — set up rooms, staff the day, and run the board."
          onBack={() => navigate("/board")}
        />
      ) : (
        <main style={s.layout} aria-label="Messages">
          {showList && (
            <div style={{ ...s.master, ...(isNarrow ? s.paneFull : {}) }}>
              <ChannelList selectedId={selection?.id} onSelect={pick} />
            </div>
          )}
          {showThread && (
            <div style={{ ...s.detail, ...(isNarrow ? s.paneFull : {}) }}>
              <ConversationView
                selection={selection}
                onBack={isNarrow ? () => setSelection(null) : undefined}
              />
            </div>
          )}
        </main>
      )}
    </div>
  );
}

const s = {
  page: {
    // The panes scroll inside themselves, so the page itself must not — a
    // second scrollbar on a chat surface loses the composer off the bottom.
    height: "100vh",
    background: color.surface.base,
    color: color.text.primary,
    fontFamily: font.family,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  topbar: {
    background: color.surface.card,
    borderBottom: `1px solid ${color.border.default}`,
    padding: "0 20px",
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  topbarRight: { display: "flex", alignItems: "center", gap: space.sm },

  layout: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    background: color.surface.card,
    overflow: "hidden",
  },
  master: {
    width: 300,
    flexShrink: 0,
    borderRight: `1px solid ${color.border.default}`,
    overflowY: "auto",
  },
  // minWidth:0 is load-bearing — without it a long message name refuses to
  // shrink and pushes the composer off the side of a flex row.
  detail: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" },
  paneFull: { width: "100%", flex: 1, borderRight: "none" },

  noticeWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: space.lg },
  notice: {
    maxWidth: 440,
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.lg,
    padding: space.xl,
    textAlign: "center",
  },
  noticeTitle: {
    margin: `0 0 ${space.sm}px`,
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
    lineHeight: font.lineHeight.tight,
  },
  noticeBody: {
    margin: `0 0 ${space.lg}px`,
    fontSize: font.size.body,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
  },
  cta: {
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.md,
    padding: "10px 20px",
    fontSize: font.size.body,
    fontWeight: font.weight.semibold,
    fontFamily: "inherit",
    minHeight: 44,
  },
};
